import { P } from 'components'
import BreadCrumbs from 'components/breadcrumbs'
import SvgIcon from 'components/Icon/SvgIcon'
import LoadingV2 from 'components/LoadingV2'
import PMBTabSwitcher from 'components/PMBTabSwitcher'
import { setActiveTab } from 'features/private-views/workplace/workplaceListSlice'
import { useEffect, useState } from 'react'
import { Star } from 'react-feather'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Card, CardBody, Col, Row } from 'reactstrap'
import {
  URL_CONFIGURATIONS,
  URL_FINDORA_ITEMS,
  URL_FINDORA_LOGS,
  URL_FINDORA_PRODUCT_MAPPING_SCHEMA,
  URL_MAP_SCHEMA,
  URL_MASTER_ITEMS,
  URL_REPORT_PNBP,
} from 'utils/pathUrl'
import { BUSINESS_BOOKKEEPING, READ } from 'utils/subjectActions'
import './styles.css'

const ListWorkplace = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { activeTab } = useSelector((state) => state.workplaceList)
  const [pinnedItems, setPinnedItems] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const { ownerData } = useSelector((s) => s.dashboard)
  const ownerUserId = ownerData?.userId
  const { serviceProvision } = useSelector((s) => s.auth)

  useEffect(() => {
    dispatch(
      setActiveTab(
        serviceProvision?.finProdMapping === true ? 'findora' : 'pnbp',
      ),
    )
  }, [serviceProvision?.finProdMapping])

  const getStorageKey = (userId) => `pinnedWorkplaceItems-${userId}`

  const safeParseJSON = (jsonString) => {
    try {
      return JSON.parse(jsonString)
    } catch (error) {
      console.error('Error parsing JSON from localStorage:', error)
      return null
    }
  }

  const safeSetLocalStorage = (key, data) => {
    try {
      localStorage.setItem(key, JSON.stringify(data))
      return true
    } catch (error) {
      console.error('Error saving to localStorage:', error)
      return false
    }
  }

  // LOAD PINNED ITEMS FROM LOCAL STORAGE
  useEffect(() => {
    if (!ownerUserId) {
      setIsLoading(false)
      return
    }

    const storageKey = getStorageKey(ownerUserId)

    try {
      const stored = localStorage.getItem(storageKey)

      if (stored) {
        const parsed = safeParseJSON(stored)

        if (parsed && Array.isArray(parsed)) {
          const validItems = parsed.filter(
            (item) =>
              item &&
              typeof item === 'object' &&
              item.value &&
              item.label &&
              item.path,
          )

          // FILTER OUT FINDORA ITEMS IF SERVICE PROVISION IS NOT AVAILABLE
          const filteredItems = validItems.filter((item) => {
            // CHECK IF ITEM IS FROM FINDORA DATA
            const isFindoraItem = findoraData.some(
              (findoraItem) =>
                findoraItem.value === item.value &&
                findoraItem.path === item.path,
            )

            // IF IT'S FINDORA ITEM AND SERVICE PROVISION IS FALSE, EXCLUDE IT
            if (isFindoraItem && serviceProvision?.finProdMapping !== true) {
              return false
            }

            return true
          })

          setPinnedItems(filteredItems)

          // UPDATE LOCALSTORAGE IF ITEMS WERE FILTERED OUT
          if (filteredItems.length !== validItems.length) {
            if (filteredItems.length === 0) {
              localStorage.removeItem(storageKey)
            } else {
              safeSetLocalStorage(storageKey, filteredItems)
            }
          }
        } else {
          console.warn('Invalid pinned items data in localStorage, clearing...')
          localStorage.removeItem(storageKey)
        }
      }
    } catch (error) {
      console.error('Error loading pinned items:', error)
      try {
        localStorage.removeItem(storageKey)
      } catch (removeError) {
        console.error(
          'Error removing corrupted localStorage item:',
          removeError,
        )
      }
    } finally {
      setIsLoading(false)
    }
  }, [ownerUserId, serviceProvision?.finProdMapping])

  // SAVE PINNED ITEMS TO LOCAL STORAGE WHEN IT CHANGES
  useEffect(() => {
    if (isLoading || !ownerUserId) return

    const storageKey = getStorageKey(ownerUserId)

    if (pinnedItems.length === 0) {
      try {
        localStorage.removeItem(storageKey)
        window.dispatchEvent(
          new CustomEvent('pinnedItemsChanged', {
            detail: { userId: ownerUserId, pinnedItems: [] },
          }),
        )
      } catch (error) {
        console.error('Error clearing localStorage:', error)
      }
    } else {
      const success = safeSetLocalStorage(storageKey, pinnedItems)
      if (success) {
        window.dispatchEvent(
          new CustomEvent('pinnedItemsChanged', {
            detail: { userId: ownerUserId, pinnedItems },
          }),
        )
      }
    }
  }, [pinnedItems, ownerUserId, isLoading])

  const handleTabChange = (tabKey) => {
    dispatch(setActiveTab(tabKey))
  }

  const pnbpData = [
    {
      value: 'konfigurasi-komoditas',
      label: 'Konfigurasi',
      path: URL_CONFIGURATIONS,
      icon: (
        <div
          style={{
            backgroundColor: '#E6F4EA',
            padding: '6px',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
          }}
        >
          <SvgIcon
            name="setting"
            size={24}
            color="#34A853"
            width={24}
            height={24}
          />
        </div>
      ),
      iconName: 'setting',
      action: READ,
      subject: [BUSINESS_BOOKKEEPING],
    },
    {
      value: 'laporan-pnbp',
      label: 'Laporan PNBP',
      path: URL_REPORT_PNBP,
      icon: (
        <div
          style={{
            backgroundColor: '#FDE7E9',
            padding: '6px',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
          }}
        >
          <SvgIcon
            name="report-converted"
            size={24}
            color="#EA4335"
            width={24}
            height={24}
          />
        </div>
      ),
      iconName: 'report-converted',
      action: READ,
      subject: [BUSINESS_BOOKKEEPING],
    },
  ]

  const findoraData = [
    {
      value: 'master-items',
      label: 'Manajemen Barang',
      path: URL_MASTER_ITEMS,
      icon: (
        <div
          style={{
            backgroundColor: '#E6F4EB',
            padding: '6px',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
          }}
        >
          <SvgIcon
            name="old-icons-box"
            size={24}
            color="#34A853"
            width={24}
            height={24}
          />
        </div>
      ),
      iconName: 'old-icons-box',
      action: READ,
      subject: [BUSINESS_BOOKKEEPING],
    },
    {
      value: 'product-mapping-schema',
      label: 'Pemetaan Bahan Baku',
      path: URL_FINDORA_PRODUCT_MAPPING_SCHEMA,
      icon: (
        <div
          style={{
            backgroundColor: '#F2E6F4',
            padding: '6px',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
          }}
        >
          <SvgIcon
            name="cube"
            size={24}
            color="#A8349D"
            width={24}
            height={24}
          />
        </div>
      ),
      iconName: 'cube',
      action: READ,
      subject: [BUSINESS_BOOKKEEPING],
    },
    {
      value: 'findora-logs',
      label: 'Riwayat Sinkronisasi',
      path: URL_FINDORA_LOGS,
      icon: (
        <div
          style={{
            backgroundColor: '#E6EEF4',
            padding: '6px',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
          }}
        >
          <SvgIcon
            name="old-icons-refresh"
            size={24}
            color="#347fA8"
            width={24}
            height={24}
          />
        </div>
      ),
      iconName: 'old-icons-refresh',
      action: READ,
      subject: [BUSINESS_BOOKKEEPING],
    },
  ]

  const handlePinToggle = (item, e) => {
    try {
      e.stopPropagation()
      e.preventDefault()

      // CHECK IF TRYING TO PIN FINDORA ITEM WITHOUT PERMISSION
      const isFindoraItem = findoraData.some(
        (findoraItem) =>
          findoraItem.value === item.value && findoraItem.path === item.path,
      )

      if (isFindoraItem && serviceProvision?.finProdMapping !== true) {
        console.warn(
          'Cannot pin Findora item without finProdMapping permission',
        )
        return
      }

      const itemData = {
        value: item.value,
        label: item.label,
        path: item.path,
        iconName: item.iconName,
        action: item.action,
        subject: item.subject,
        pinnedAt: Date.now(),
      }

      const itemKey = `${itemData.value}-${itemData.path}`

      setPinnedItems((currentPinned) => {
        const newPinned = [...currentPinned]
        const existingIndex = newPinned.findIndex(
          (pinned) => `${pinned.value}-${pinned.path}` === itemKey,
        )

        if (existingIndex !== -1) {
          // REMOVE FROM PINNED
          newPinned.splice(existingIndex, 1)
        } else {
          // ADD TO PINNED
          newPinned.push(itemData)
        }
        return newPinned
      })
    } catch (error) {
      console.error('Pin toggle error:', error)
    }
  }

  const isItemPinned = (item) => {
    if (!item || !item.value || !item.path) return false

    const itemKey = `${item.value}-${item.path}`
    return pinnedItems.some(
      (pinned) => pinned && `${pinned.value}-${pinned.path}` === itemKey,
    )
  }

  // FUNCTION TO GET ICON FOR PINNED ITEM
  const getIconForPinnedItem = (item) => {
    if (!item) return null

    // FIND THE ORIGINAL ITEM WITH ICON FROM PNBPDATA OR FINDORADATA
    const allItems = [...pnbpData, ...findoraData]
    const originalItem = allItems.find(
      (original) =>
        original.value === item.value && original.path === item.path,
    )
    return originalItem ? originalItem.icon : null
  }

  const renderCard = (
    item,
    index,
    showPinButton = true,
    isPinnedSection = false,
  ) => {
    if (!item) return null

    // GET ICON FOR PINNED SECTION
    const displayIcon = isPinnedSection ? getIconForPinnedItem(item) : item.icon

    return (
      <Col
        key={`${item.value}-${item.path}-${index}`}
        className="mb-3"
        xs="12"
        sm="6"
        lg="4"
      >
        <Card
          className="border-0 shadow-sm card-hover position-relative h-100"
          onClick={(e) => {
            if (!e.target.closest('.pin-button')) {
              navigate(item.path, { state: { from: 'workplace' } })
            }
          }}
          style={{ cursor: 'pointer', border: '1px solid #ebe9f1' }}
          data-testid={`workplace-card-${item.value}`}
        >
          <CardBody className="p-2 d-flex align-items-center justify-content-between gap-2">
            <div className="d-flex align-items-center gap-2">
              {displayIcon}
              <P className="mb-0 fw-medium" style={{ fontSize: '0.95rem' }}>{item.label}</P>
            </div>
            {showPinButton && (
              <div
                className="pin-button ms-auto"
                onClick={(e) => handlePinToggle(item, e)}
                style={{
                  cursor: 'pointer',
                  padding: '6px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  backgroundColor: isItemPinned(item)
                    ? '#FFF3CD'
                    : 'transparent',
                }}
                data-testid={`workplace-pin-${item.value}`}
              >
                <Star
                  size={16}
                  style={{
                    color: isItemPinned(item) ? '#FFC107' : '#6C757D',
                    fill: isItemPinned(item) ? '#FFC107' : 'none',
                  }}
                />
              </div>
            )}
          </CardBody>
        </Card>
      </Col>
    )
  }

  if (isLoading) {
    return <LoadingV2 />
  }

  return (
    <div className="w-100 px-3">
      <div className="mt-3">
        <BreadCrumbs breadCrumbActive="Alat Bantu" />
      </div>

      {/* FAVORITE SECTION */}
      {pinnedItems?.length > 0 && (
        <Card className="mb-3 border-0 shadow-none">
          <CardBody>
            <div className="mb-2 d-flex align-items-center gap-2">
              <Star size={20} style={{ color: '#FFC107', fill: '#FFC107' }} />
              <P size="large" className="fw-semibold mb-0">
                Favorit ({pinnedItems.length})
              </P>
            </div>
            <Row>
              {pinnedItems
                ?.filter((item) => item)
                ?.sort((a, b) => (b.pinnedAt || 0) - (a.pinnedAt || 0))
                ?.map((item, index) => renderCard(item, index, true, true))}
            </Row>
          </CardBody>
        </Card>
      )}

      <Card className="mb-3 border-0 shadow-none">
        <CardBody>
          <div className="mb-4">
            <P size="large" className="fw-semibold">
              Alat Bantu
            </P>
          </div>

          <PMBTabSwitcher
            activeTab={activeTab}
            tabs={[
              { key: 'pnbp', label: 'PNBP' },
              ...(serviceProvision?.finProdMapping === true
                ? [{ key: 'findora', label: 'Findora' }]
                : []),
            ]}
            onTabChange={handleTabChange}
            testId="workplace-tab"
          />

          {activeTab === 'pnbp' && (
            <Row>{pnbpData?.map((item, index) => renderCard(item, index))}</Row>
          )}

          {activeTab === 'findora' &&
            serviceProvision?.finProdMapping === true && (
              <Row>
                {findoraData?.map((item, index) => renderCard(item, index))}
              </Row>
            )}
        </CardBody>
      </Card>
    </div>
  )
}

export default ListWorkplace
